import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
  Filler,
} from "chart.js";
import { Divider } from "antd";

ChartJS.register(
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
  Filler,
);

function MergedChart({
  dataPoints = [],
  anwerDataPoints = [],
  updateVideoTrack,
  avgAnswerTime,
  jdAvgTime,
  avgThinkingTime,
  jdAvgThinkingTime,
  minThinkingTime,
  maxThinkingTime,
  minAnswerTime,
  maxAnswerTime,
}) {
  // dataPoints = []
  const hasThinkingData = dataPoints?.length > 0;
  const hasAnsweringData = anwerDataPoints?.length > 0;

  if (!hasThinkingData && !hasAnsweringData) {
    return (
      <div className="min-h-[100px] flex items-center justify-center text-gray-500 text-base">
        No data points found.
      </div>
    );
  }

  const thinkingValues = dataPoints?.map((point) => ({
    x: point.elapsedTime,
    y: point.userThinkingTime,
  }));

  const answeringValues = anwerDataPoints?.map((point) => ({
    x: point.elapsedTime,
    y: point.answeringTime,
  }));

  const maxX = Math.max(
    hasThinkingData ? Math.max(...dataPoints.map((p) => p.elapsedTime)) : 0,
    hasAnsweringData
      ? Math.max(...anwerDataPoints.map((p) => p.elapsedTime))
      : 0,
  );

  const allY = [
    ...(hasThinkingData ? dataPoints.map((p) => p.userThinkingTime) : []),
    ...(hasAnsweringData ? anwerDataPoints.map((p) => p.answeringTime) : []),
  ];

  const maxYValue = allY.length ? Math.max(...allY) : 10;
  const maxY = Math.ceil(maxYValue / 5) * 5 + 5;

  const convertSecondsToTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const createHorizontalLine = (value, label, color, dashed = true) => ({
    label,
    data: [
      { x: 0, y: value },
      { x: maxX + 10, y: value },
    ],
    borderColor: color,
    borderWidth: 2,
    borderDash: dashed ? [6, 6] : [],
    pointRadius: 0,
    fill: false,
    tension: 0,
  });

  const datasets = [];

  if (hasAnsweringData) {
    datasets.push({
      label: "Answering Time",
      data: answeringValues,
      borderColor: "#9333ea",
      backgroundColor: "rgba(37,99,235,0.1)",
      borderWidth: 2,
      fill: true,
      tension: 0.3,
      pointRadius: (context) => {
        const value = context.parsed.y;
        if (value === minAnswerTime || value === maxAnswerTime) return 6;
        return 4;
      },
      pointBackgroundColor: (context) => {
        const value = context.parsed.y;
        if (value === minAnswerTime) return "#ef4444";
        if (value === maxAnswerTime) return "#22c55e";
        return "#9333ea";
      },
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
    });
  }

  if (hasThinkingData) {
    datasets.push({
      label: "Thinking Time",
      data: thinkingValues,
      borderColor: "#2563eb",
      backgroundColor: "rgba(147,51,234,0.1)",
      borderWidth: 2,
      fill: true,
      tension: 0.3,
      pointRadius: (context) => {
        const value = context.parsed.y;
        if (value === minThinkingTime || value === maxThinkingTime) return 6;
        return 4;
      },
      pointBackgroundColor: (context) => {
        const value = context.parsed.y;
        if (value === minThinkingTime) return "#ef4444";
        if (value === maxThinkingTime) return "#22c55e";
        return "rgb(32,109,197)";
      },
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
    });
  }

  if (hasAnsweringData || hasThinkingData) {
    datasets.push({
      label: "Minimum",
      borderColor: "#ef4444",
      backgroundColor: "#ef4444",
      pointRadius: 6,
      showLine: false,
    });
    datasets.push({
      label: "Maximum",
      borderColor: "#22c55e",
      backgroundColor: "#22c55e",
      pointRadius: 6,
      showLine: false,
    });
  }

  if (hasAnsweringData) {
    datasets.push(
      createHorizontalLine(avgAnswerTime, "Answer Avg", "#85E13A", false),
    );
    datasets.push(
      createHorizontalLine(jdAvgTime, "Answer JD Avg", "#FDB022", true),
    );
  }

  if (hasThinkingData) {
    datasets.push(
      createHorizontalLine(avgThinkingTime, "Thinking Avg", "#c2410c", false),
    );
    datasets.push(
      createHorizontalLine(
        jdAvgThinkingTime,
        "Thinking JD Avg",
        "#EE46BC",
        true,
      ),
    );
  }

  const data = {
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: maxX + 10,
        title: {
          display: true,
          text: "Elapsed Time (mm:ss)",
          font: { weight: "bold" },
        },
        ticks: {
          callback: (value) => convertSecondsToTime(value),
        },
      },
      y: {
        min: -5,
        max: maxY,
        title: {
          display: true,
          text: "Time (seconds)",
          font: { weight: "bold" },
        },
        ticks: {
          stepSize: 5,
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "User Performance Analysis",
        font: {
          size: 18,
          weight: "bold",
        },
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      legend: {
        display: true,
        position: "top",
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const datasetIndex = legendItem.datasetIndex;
          const label = legendItem.text;

          // Prevent click for Maximum (and Minimum if you want)
          if (label === "Maximum" || label === "Minimum") {
            return;
          }

          // Default toggle behavior for others
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.hidden =
            meta.hidden === null
              ? !chart.data.datasets[datasetIndex].hidden
              : null;

          chart.update();
        },
      },
      tooltip: {
        callbacks: {
          title: (context) =>
            `Elapsed: ${convertSecondsToTime(context[0].parsed.x)}`,
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toFixed(2)} sec`,
        },
      },
    },
    interaction: {
      mode: "nearest",
      intersect: true,
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && updateVideoTrack) {
        const clickedX = elements[0].element.$context.parsed.x;
        updateVideoTrack(clickedX);

        window.scrollTo({
          top: 200,
          behavior: "smooth",
        });
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
  };

  return (
    <div>
      <div className="min-h-[450px]">
        <Line data={data} options={options} />
      </div>
      <div className="mt-5 max-w-[88rem] mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="mb-[.35rem]">
          <span className="font-semibold">How to read:</span> This graph shows
          the combined thinking and answering time taken by the user, providing
          an overall view of the user's response performance during the
          interview. Refer to the{" "}
          <span className="font-semibold">Individual Graphs</span> for clearer
          interpretation of thinking time and answering time separately.
        </p>

        <p className="text-blue-800">
          <span className="font-semibold">Note:</span> The JD-level averages are
          also displayed to help compare the user's average times with the overall
          averages for the same role.
        </p>
      </div>
      <Divider className="border-gray-200 mt-8" />
    </div>
  );
}

export default MergedChart;
